import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, Plus, RefreshCw, Save, Shield, Trash2, Users } from 'lucide-react';

import { useDarkMode } from '../contexts/DarkModeContext';
import { useAuth } from '../services/AuthContext';
import {
  SESSION_ACCESS_OPTIONS,
  type SessionAccessLevel,
  type SessionAccessResponse,
  authorizedJsonRequest,
  formatDateTime,
  roleLabel,
} from './permissionsShared';

interface SessionAccessManagerProps {
  sessionId: string;
  sessionTitle?: string;
  isActive: boolean;
}

interface GrantDraft {
  id?: string;
  email: string;
  accessLevel: SessionAccessLevel;
  grantedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

function buildGrantDrafts(data: SessionAccessResponse | null): GrantDraft[] {
  if (!data) return [];
  return data.grants.map((grant) => ({
    id: grant.id,
    email: grant.email,
    accessLevel: grant.accessLevel,
    grantedBy: grant.grantedBy,
    createdAt: grant.createdAt,
    updatedAt: grant.updatedAt,
  }));
}

const SessionAccessManager: React.FC<SessionAccessManagerProps> = ({
  sessionId,
  sessionTitle,
  isActive,
}) => {
  const { token } = useAuth();
  const { isDarkMode } = useDarkMode();
  const [accessState, setAccessState] = useState<SessionAccessResponse | null>(null);
  const [ownerEmailDraft, setOwnerEmailDraft] = useState('');
  const [grantDrafts, setGrantDrafts] = useState<GrantDraft[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const canManage = !!accessState?.canManage;

  const isDirty = useMemo(() => {
    if (!accessState) return false;

    const normalizedOwner = ownerEmailDraft.trim().toLowerCase();
    const currentOwner = (accessState.ownerEmail || '').trim().toLowerCase();
    if (normalizedOwner !== currentOwner) return true;

    const original = buildGrantDrafts(accessState)
      .map((grant) => `${grant.email.toLowerCase()}|${grant.accessLevel}`)
      .sort();
    const draft = grantDrafts
      .filter((grant) => grant.email.trim())
      .map((grant) => `${grant.email.trim().toLowerCase()}|${grant.accessLevel}`)
      .sort();

    if (original.length !== draft.length) return true;
    return original.some((entry, index) => entry !== draft[index]);
  }, [accessState, grantDrafts, ownerEmailDraft]);

  const loadAccessState = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setError('');

    try {
      const response = await authorizedJsonRequest<SessionAccessResponse>(
        `/api/sessions/${sessionId}/access`,
        token
      );
      setAccessState(response);
      setOwnerEmailDraft(response.ownerEmail || '');
      setGrantDrafts(buildGrantDrafts(response));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load access settings');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, token]);

  useEffect(() => {
    if (!isActive || !sessionId || !token) return;
    void loadAccessState();
  }, [isActive, sessionId, token, loadAccessState]);

  const saveAccessState = async () => {
    if (!token || !canManage) return;

    const normalizedOwner = ownerEmailDraft.trim().toLowerCase();
    if (!normalizedOwner) {
      setError('Owner email is required.');
      return;
    }

    const seenEmails = new Set<string>();
    const normalizedGrants = [];
    for (const grant of grantDrafts) {
      const email = grant.email.trim().toLowerCase();
      if (!email) continue;
      if (seenEmails.has(email)) {
        setError(`Duplicate access entry for ${email}.`);
        return;
      }
      seenEmails.add(email);
      normalizedGrants.push({
        email,
        accessLevel: grant.accessLevel,
      });
    }

    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await authorizedJsonRequest<SessionAccessResponse>(
        `/api/sessions/${sessionId}/access`,
        token,
        {
          method: 'PUT',
          body: JSON.stringify({
            ownerEmail: normalizedOwner,
            grants: normalizedGrants,
          }),
        }
      );
      setAccessState(response);
      setOwnerEmailDraft(response.ownerEmail || '');
      setGrantDrafts(buildGrantDrafts(response));
      setSuccess('Session access updated.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update access settings');
    } finally {
      setIsSaving(false);
    }
  };

  const updateGrantDraft = (index: number, updater: (current: GrantDraft) => GrantDraft) => {
    setGrantDrafts((current) => current.map((grant, grantIndex) => (grantIndex === index ? updater(grant) : grant)));
  };

  const removeGrantDraft = (index: number) => {
    setGrantDrafts((current) => current.filter((_, grantIndex) => grantIndex !== index));
  };

  const surfaceClass = isDarkMode
    ? 'border-[#344052] bg-[#202A3A]/92 text-[#F4F4F4] shadow-[0_24px_44px_-30px_rgba(10,14,20,0.78)]'
    : 'border-[#D6DCE4] bg-white/[0.94] text-[#232E3F] shadow-[0_22px_42px_-30px_rgba(35,46,63,0.28)]';

  const nestedSurfaceClass = isDarkMode
    ? 'border-[#41506A] bg-[#273244]/90'
    : 'border-[#E4D7BD] bg-[#FFF8EA]/90';

  const secondaryTextClass = isDarkMode ? 'text-[#C6CDD8]' : 'text-[#566377]';
  const inputClass = `w-full rounded-xl border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#FFB52B]/25 focus:border-[#FFB52B] ${
    isDarkMode
      ? 'border-[#445166] bg-[#1D2634] text-[#F4F4F4] placeholder:text-[#8693A6]'
      : 'border-[#CDD4DD] bg-white text-[#232E3F] placeholder:text-[#93A0B1]'
  }`;

  if (isLoading && !accessState) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-[#FFB52B]" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {(error || success) && (
        <div
          className={`mb-4 rounded-2xl border p-4 ${
            error
              ? isDarkMode
                ? 'border-[#8B4C4C] bg-[#331E21] text-[#F8C9C9]'
                : 'border-[#E6BBBB] bg-[#FFF2F1] text-[#9B4343]'
              : isDarkMode
                ? 'border-[#8F6A2B] bg-[#302717] text-[#FFE3A1]'
                : 'border-[#E7B14C] bg-[#FFF4D8] text-[#8B5B00]'
          }`}
        >
          <div className="flex items-start gap-3">
            {error ? <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" /> : <Check className="mt-0.5 h-5 w-5 shrink-0" />}
            <span className="text-sm">{error || success}</span>
          </div>
        </div>
      )}

      <div className={`rounded-3xl border p-5 ${surfaceClass}`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className={`mb-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${nestedSurfaceClass}`}>
              <Shield className="h-3.5 w-3.5" />
              Session Access
            </div>
            <h3 className="text-xl font-semibold">{sessionTitle || 'Session access management'}</h3>
            <p className={`mt-2 text-sm ${secondaryTextClass}`}>
              Owners, org admins, and explicit session grants determine who can read, edit, or manage this session.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void loadAccessState()}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                isDarkMode
                  ? 'border border-[#41506A] bg-[#273244] text-[#F4F4F4] hover:bg-[#2F3B4E]'
                  : 'border border-[#D6DCE4] bg-white text-[#232E3F] hover:border-[#FFB52B]/50 hover:bg-[#FFF8EA]'
              }`}
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {canManage && (
              <button
                type="button"
                onClick={saveAccessState}
                disabled={!isDirty || isSaving}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                  !isDirty || isSaving
                    ? isDarkMode
                      ? 'cursor-not-allowed bg-[#273244] text-[#7C8899]'
                      : 'cursor-not-allowed bg-[#EEF1F3] text-[#98A4B4]'
                    : 'bg-[#FFB52B] text-[#232E3F] shadow-[0_18px_32px_-24px_rgba(255,181,43,0.9)] hover:bg-[#FFC54D]'
                }`}
              >
                <Save className="h-4 w-4" />
                {isSaving ? 'Saving…' : 'Save Access'}
              </button>
            )}
          </div>
        </div>

        {accessState && (
          <div className="mt-6 grid gap-3 md:grid-cols-3">
            <InfoCard
              isDarkMode={isDarkMode}
              icon={<Shield className="h-5 w-5" />}
              label="Your Session Access"
              value={
                accessState.currentUserAccess.canManage
                  ? 'Manage'
                  : accessState.currentUserAccess.canWrite
                    ? 'Write'
                    : 'Read'
              }
              hint={`Role: ${roleLabel(accessState.currentUserAccess.role)}`}
            />
            <InfoCard
              isDarkMode={isDarkMode}
              icon={<Users className="h-5 w-5" />}
              label="Owner"
              value={accessState.ownerEmail || 'Unassigned'}
              hint={
                accessState.currentUserAccess.isOwner
                  ? 'You own this session'
                  : accessState.currentUserAccess.isOrgAdmin
                    ? 'Org-admin access applies'
                    : 'Owner retains manage access'
              }
            />
            <InfoCard
              isDarkMode={isDarkMode}
              icon={<Users className="h-5 w-5" />}
              label="Explicit Grants"
              value={String(accessState.grants.length)}
              hint={canManage ? 'Grant read, write, or manage access' : 'Read-only view'}
            />
          </div>
        )}
      </div>

      {!accessState ? null : (
        <div className={`mt-4 rounded-3xl border p-5 ${surfaceClass}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="text-lg font-semibold">Access List</h4>
              <p className={`mt-1 text-sm ${secondaryTextClass}`}>
                Direct grants are additive. Organization membership still governs broad baseline visibility.
              </p>
            </div>
          </div>

          {canManage ? (
            <>
              <div className={`mt-5 rounded-2xl border p-4 ${nestedSurfaceClass}`}>
                <label className="block text-sm font-medium">Owner email</label>
                <input
                  type="email"
                  value={ownerEmailDraft}
                  onChange={(event) => setOwnerEmailDraft(event.target.value)}
                  className={`${inputClass} mt-2`}
                  placeholder="owner@example.com"
                />
                <p className={`mt-2 text-xs ${secondaryTextClass}`}>
                  Ownership carries manage access even without an explicit grant.
                </p>
              </div>

              <div className="mt-5 space-y-3">
                {grantDrafts.length === 0 ? (
                  <div className={`rounded-2xl border p-4 ${nestedSurfaceClass}`}>
                    <p className={secondaryTextClass}>No explicit grants yet.</p>
                  </div>
                ) : (
                  grantDrafts.map((grant, index) => (
                    <div
                      key={grant.id || `${grant.email}-${index}`}
                      className={`rounded-2xl border p-4 ${nestedSurfaceClass}`}
                    >
                      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_180px_auto]">
                        <div>
                          <label className="block text-sm font-medium">Member email</label>
                          <input
                            type="email"
                            value={grant.email}
                            onChange={(event) =>
                              updateGrantDraft(index, (current) => ({
                                ...current,
                                email: event.target.value,
                              }))
                            }
                            className={`${inputClass} mt-2`}
                            placeholder="person@example.com"
                          />
                          {(grant.grantedBy || grant.updatedAt || grant.createdAt) && (
                            <p className={`mt-2 text-xs ${secondaryTextClass}`}>
                              {grant.grantedBy ? `Granted by ${grant.grantedBy}` : 'Explicit grant'}{' '}
                              {grant.updatedAt || grant.createdAt
                                ? `· ${formatDateTime(grant.updatedAt || grant.createdAt)}`
                                : ''}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium">Access level</label>
                          <select
                            value={grant.accessLevel}
                            onChange={(event) =>
                              updateGrantDraft(index, (current) => ({
                                ...current,
                                accessLevel: event.target.value as SessionAccessLevel,
                              }))
                            }
                            className={`${inputClass} mt-2`}
                          >
                            {SESSION_ACCESS_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => removeGrantDraft(index)}
                            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                              isDarkMode
                                ? 'bg-[#3A2024] text-[#F1C3C6] hover:bg-[#4A2A2E]'
                                : 'bg-[#FCEBEB] text-[#964E4E] hover:bg-[#F7DADA]'
                            }`}
                          >
                            <Trash2 className="h-4 w-4" />
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="mt-4">
                <button
                  type="button"
                  onClick={() =>
                    setGrantDrafts((current) => [
                      ...current,
                      {
                        email: '',
                        accessLevel: 'read',
                      },
                    ])
                  }
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-[#232E3F] text-[#F4F4F4] hover:bg-[#2F3B4E]'
                      : 'bg-[#232E3F] text-[#F4F4F4] hover:bg-[#2F3B4E]'
                  }`}
                >
                  <Plus className="h-4 w-4" />
                  Add Grant
                </button>
              </div>
            </>
          ) : (
            <div className="mt-5 space-y-3">
              {accessState.grants.length === 0 ? (
                <div className={`rounded-2xl border p-4 ${nestedSurfaceClass}`}>
                  <p className={secondaryTextClass}>No explicit session grants.</p>
                </div>
              ) : (
                accessState.grants.map((grant) => (
                  <div
                    key={grant.id}
                    className={`rounded-2xl border p-4 ${nestedSurfaceClass}`}
                  >
                    <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                      <div>
                        <p className="font-medium">{grant.name || grant.email}</p>
                        <p className={`text-sm ${secondaryTextClass}`}>{grant.email}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className={`rounded-full border px-3 py-1 ${
                          isDarkMode
                            ? 'border-[#8F6A2B]/40 bg-[#FFB52B]/[0.16] text-[#FFE3A1]'
                            : 'border-[#FFB52B]/40 bg-[#FFF4D8] text-[#8B5B00]'
                        }`}>
                          {grant.accessLevel}
                        </span>
                        {grant.grantedBy ? (
                          <span className={secondaryTextClass}>Granted by {grant.grantedBy}</span>
                        ) : null}
                        {grant.updatedAt || grant.createdAt ? (
                          <span className={secondaryTextClass}>{formatDateTime(grant.updatedAt || grant.createdAt)}</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function InfoCard({
  isDarkMode,
  icon,
  label,
  value,
  hint,
}: {
  isDarkMode: boolean;
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className={`rounded-2xl border p-4 ${isDarkMode ? 'border-[#41506A] bg-[#273244]/88' : 'border-[#E4D7BD] bg-[#FFF8EA]/90'}`}>
      <div className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl ${isDarkMode ? 'bg-[#1D2634] text-[#FFB52B]' : 'bg-white text-[#232E3F] shadow-[0_10px_18px_-16px_rgba(35,46,63,0.7)]'}`}>
        {icon}
      </div>
      <p className={`text-sm ${isDarkMode ? 'text-[#C6CDD8]' : 'text-[#566377]'}`}>{label}</p>
      <p className="mt-1 break-all text-base font-semibold">{value}</p>
      {hint ? <p className={`mt-1 text-xs ${isDarkMode ? 'text-[#8D99A8]' : 'text-[#7A8593]'}`}>{hint}</p> : null}
    </div>
  );
}

export default SessionAccessManager;
